import { Head, useForm } from "@inertiajs/react"
import type { ReactNode } from "react"
import { useState } from "react"
import MatchLayout from "@/layouts/match-layout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Upload, Video } from "lucide-react"

export default function StaffMatchesNew() {
  const [mode, setMode] = useState<"csv" | "video" | "match_csv" | "stats_csv">("csv")
  const [dragActive, setDragActive] = useState(false)
  const [fileName, setFileName] = useState<string | null>(null)

  const csvForm = useForm({
    csv_file: null as File | null,
  })

  const videoForm = useForm({
    video_url: "",
    home_team: "",
    away_team: "",
    date: new Date().toISOString().split("T")[0],
    kickoff: "15:00",
    venue: "",
    competition: "",
  })

  const matchCsvForm = useForm({
    csv_file: null as File | null,
  })

  const statsCsvForm = useForm({
    csv_file: null as File | null,
  })

  function handleCsvSubmit(e: React.FormEvent) {
    e.preventDefault()
    csvForm.post("/staff/matches")
  }

  function handleVideoSubmit(e: React.FormEvent) {
    e.preventDefault()
    videoForm.post("/staff/matches/from_video")
  }

  function handleMatchCsvSubmit(e: React.FormEvent) {
    e.preventDefault()
    matchCsvForm.post("/staff/matches/import_csv")
  }

  function handleStatsCsvSubmit(e: React.FormEvent) {
    e.preventDefault()
    statsCsvForm.post("/staff/matches/import_stats")
  }

  function handleFile(file: File, formType: "csv" | "match_csv" | "stats_csv") {
    if (formType === "csv") csvForm.setData("csv_file", file)
    else if (formType === "match_csv") matchCsvForm.setData("csv_file", file)
    else statsCsvForm.setData("csv_file", file)
    setFileName(file.name)
  }

  const modeButtons = [
    { key: "csv" as const, label: "CSV Import", icon: <Upload className="h-4 w-4" /> },
    { key: "video" as const, label: "From Video", icon: <Video className="h-4 w-4" /> },
    { key: "match_csv" as const, label: "Match CSV", icon: <Upload className="h-4 w-4" /> },
    { key: "stats_csv" as const, label: "Stats CSV", icon: <Upload className="h-4 w-4" /> },
  ]

  return (
    <>
      <Head title="New Match" />
      <div className="border-b bg-card">
        <div className="mx-auto max-w-2xl px-4 py-6">
          <h1 className="text-2xl font-bold tracking-tight">New Match</h1>
          <p className="mt-1 text-sm text-muted-foreground">Import from CSV or analyze a video</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {modeButtons.map(btn => (
              <button
                key={btn.key}
                onClick={() => { setMode(btn.key); setFileName(null) }}
                className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                  mode === btn.key ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"
                }`}
              >
                {btn.icon}
                {btn.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-2xl px-4 py-6">
        {mode === "csv" && (
          <form onSubmit={handleCsvSubmit} className="flex flex-col gap-6">
            <DragDropZone
              dragActive={dragActive}
              setDragActive={setDragActive}
              onFile={(f) => handleFile(f, "csv")}
              fileName={mode === "csv" ? fileName : null}
            />
            {csvForm.errors.csv_file && <p className="text-sm text-destructive">{csvForm.errors.csv_file}</p>}
            <details className="rounded-lg border bg-card">
              <summary className="cursor-pointer px-4 py-3 text-sm font-medium">CSV Format Reference</summary>
              <div className="border-t px-4 py-3">
                <pre className="overflow-x-auto whitespace-pre-wrap text-xs text-muted-foreground"><code>{`section,key,value_1,value_2,value_3,value_4,value_5
match,competition,Local League – Round 6
match,venue,Club Field Main Pitch
match,date,2025-04-12
match,kickoff,15:00
match,home_team,SHK
match,away_team,HWK
match,home_score,28
match,away_score,17
event,3,home,try,S. van der Berg
stat,carries_metres,420,310`}</code></pre>
              </div>
            </details>
            <Button type="submit" disabled={csvForm.processing || !csvForm.data.csv_file}>
              {csvForm.processing ? "Importing..." : "Import Match"}
            </Button>
          </form>
        )}

        {mode === "video" && (
          <form onSubmit={handleVideoSubmit} className="flex flex-col gap-6">
            {videoForm.hasErrors && (
              <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3">
                {Object.entries(videoForm.errors).map(([key, val]) => (
                  <p key={key} className="text-sm text-destructive">{key}: {String(val)}</p>
                ))}
              </div>
            )}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Video className="h-5 w-5" />
                  Video Analysis
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4">
                <div className="rounded-md bg-muted p-3 text-xs text-muted-foreground">
                  Paste a direct link to the match video. The AI will extract frames,
                  analyze them with vision, and auto-draft scores, events, and stats.
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="video_url">Video URL</Label>
                  <Input
                    id="video_url"
                    name="video_url"
                    type="url"
                    placeholder="https://example.com/match-video.mp4"
                    value={videoForm.data.video_url}
                    onChange={(e) => videoForm.setData("video_url", e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>Match Details</CardTitle></CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="home_team">Home Team (short name)</Label>
                  <Input id="home_team" name="home_team" placeholder="e.g. SHK" value={videoForm.data.home_team} onChange={(e) => videoForm.setData("home_team", e.target.value)} />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="away_team">Away Team (short name)</Label>
                  <Input id="away_team" name="away_team" placeholder="e.g. HWK" value={videoForm.data.away_team} onChange={(e) => videoForm.setData("away_team", e.target.value)} />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="date">Date</Label>
                  <Input id="date" name="date" type="date" value={videoForm.data.date} onChange={(e) => videoForm.setData("date", e.target.value)} />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="kickoff">Kickoff</Label>
                  <Input id="kickoff" name="kickoff" value={videoForm.data.kickoff} onChange={(e) => videoForm.setData("kickoff", e.target.value)} />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="venue">Venue</Label>
                  <Input id="venue" name="venue" value={videoForm.data.venue} onChange={(e) => videoForm.setData("venue", e.target.value)} />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="competition">Competition</Label>
                  <Input id="competition" name="competition" value={videoForm.data.competition} onChange={(e) => videoForm.setData("competition", e.target.value)} />
                </div>
              </CardContent>
            </Card>
            <Button type="submit" disabled={videoForm.processing || !videoForm.data.video_url}>
              <Video className="mr-2 h-4 w-4" />
              {videoForm.processing ? "Creating & analyzing..." : "Create Match & Analyze Video"}
            </Button>
          </form>
        )}

        {mode === "match_csv" && (
          <form onSubmit={handleMatchCsvSubmit} className="flex flex-col gap-6">
            <DragDropZone
              dragActive={dragActive}
              setDragActive={setDragActive}
              onFile={(f) => handleFile(f, "match_csv")}
              fileName={mode === "match_csv" ? fileName : null}
            />
            {matchCsvForm.errors.csv_file && <p className="text-sm text-destructive">{matchCsvForm.errors.csv_file}</p>}
            {matchCsvForm.errors.base && (
              <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3">
                {Array.isArray(matchCsvForm.errors.base) ? matchCsvForm.errors.base.map((e: string, i: number) => <p key={i} className="text-sm text-destructive">{e}</p>) : Object.values(matchCsvForm.errors.base).flat().map((e: unknown, i: number) => <p key={i} className="text-sm text-destructive">{String(e)}</p>)}
              </div>
            )}
            <details className="rounded-lg border bg-card">
              <summary className="cursor-pointer px-4 py-3 text-sm font-medium">CSV Format Reference</summary>
              <div className="border-t px-4 py-3">
                <pre className="overflow-x-auto whitespace-pre-wrap text-xs text-muted-foreground"><code>{`MatchID,Fecha,Competencia,DescCompetencia,Equipo,DescEquipo,Rival,DescRival,CondicionPB,PuntosPB,PuntosRival,Resultado,Diferencia_Puntos,Local_Raw,Visitante_Raw,Resultado_Raw
20260220_AMI_JCC_PB,2/20/2026,AMI,Amistoso,PB,Palermo Bajo,JCC,Jockey Club Córdoba,Visitante,19,3,Ganó PB,16,JCC,PB,3-19`}</code></pre>
              </div>
            </details>
            <Button type="submit" disabled={matchCsvForm.processing || !matchCsvForm.data.csv_file}>
              {matchCsvForm.processing ? "Importing..." : "Import Matches"}
            </Button>
          </form>
        )}

        {mode === "stats_csv" && (
          <form onSubmit={handleStatsCsvSubmit} className="flex flex-col gap-6">
            <DragDropZone
              dragActive={dragActive}
              setDragActive={setDragActive}
              onFile={(f) => handleFile(f, "stats_csv")}
              fileName={mode === "stats_csv" ? fileName : null}
            />
            {statsCsvForm.errors.csv_file && <p className="text-sm text-destructive">{statsCsvForm.errors.csv_file}</p>}
            {statsCsvForm.errors.base && (
              <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3">
                {Array.isArray(statsCsvForm.errors.base) ? statsCsvForm.errors.base.map((e: string, i: number) => <p key={i} className="text-sm text-destructive">{e}</p>) : Object.values(statsCsvForm.errors.base).flat().map((e: unknown, i: number) => <p key={i} className="text-sm text-destructive">{String(e)}</p>)}
              </div>
            )}
            <details className="rounded-lg border bg-card">
              <summary className="cursor-pointer px-4 py-3 text-sm font-medium">CSV Format Reference</summary>
              <div className="border-t px-4 py-3">
                <pre className="overflow-x-auto whitespace-pre-wrap text-xs text-muted-foreground"><code>{`MatchID,POSICION,Grupo Posicional,DNI,TIEMPO,DUELO GANADO,DUELO PERDIDO,TACKLES CORRECTOS,TACKLES ERRADOS,RUCK OFENSIVO PASADOR,RUCK OFENSIVO APOYADOR,RUCK DEFENSIVO,QUIEBRE
20260411_TRG_CP_PB,1,1 Linea,41625834,55,1,0,9,1,3,0,1,0`}</code></pre>
              </div>
            </details>
            <Button type="submit" disabled={statsCsvForm.processing || !statsCsvForm.data.csv_file}>
              {statsCsvForm.processing ? "Importing..." : "Import Player Stats"}
            </Button>
          </form>
        )}
      </div>
    </>
  )
}

function DragDropZone({ dragActive, setDragActive, onFile, fileName }: {
  dragActive: boolean
  setDragActive: (v: boolean) => void
  onFile: (f: File) => void
  fileName: string | null
}) {
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragActive(true) }}
      onDragLeave={() => setDragActive(false)}
      onDrop={(e) => { e.preventDefault(); setDragActive(false); if (e.dataTransfer.files[0]) onFile(e.dataTransfer.files[0]) }}
      className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-12 transition-colors ${
        dragActive ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/50"
      }`}
    >
      <svg className="mb-3 h-10 w-10 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
      </svg>
      {fileName ? (
        <p className="text-sm font-medium">{fileName}</p>
      ) : (
        <p className="text-sm text-muted-foreground">Drag & drop a CSV file here, or click to browse</p>
      )}
      <input
        type="file"
        accept=".csv"
        className="absolute inset-0 cursor-pointer opacity-0"
        style={{ position: "relative", marginTop: "0.75rem" }}
        onChange={(e) => { if (e.target.files?.[0]) onFile(e.target.files[0]) }}
      />
    </div>
  )
}

StaffMatchesNew.layout = (page: ReactNode) => <MatchLayout>{page}</MatchLayout>
